import React, { FormEvent, useState } from "react";
import axios from "axios";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Button,
  FormErrorMessage,
  useToast,
} from "@chakra-ui/react";
import { useCustomTheme } from "../../contexts/CustomThemeContext";
import AppPhoneInput, {
  AppPhoneCountryCode,
  buildInternationalNumber,
} from "../../components/Shared/PhoneInput";
type Props = {
  isOpen: boolean;
  onClose: () => void;
  subject?: string;
};
type NotifyForm = {
  fullName: string;
  email: string;
  phone: string;
  message: string;
};
type TouchedState = Record<keyof NotifyForm, boolean>;
type ValidationErrors = Record<keyof NotifyForm, string>;
const EMPTY_FORM: NotifyForm = {
  fullName: "",
  email: "",
  phone: "",
  message: "",
};
const EMPTY_TOUCHED: TouchedState = {
  fullName: false,
  email: false,
  phone: false,
  message: false,
};
const normalizeSpaces = (value: string) => value.trim().replace(/\s+/g, " ");
const getFullNameError = (value: string) => {
  const normalized = normalizeSpaces(value);
  if (!normalized) return "Full name is required.";
  if (normalized.length < 2) return "Full name must be at least 2 characters.";
  if (normalized.length > 100) return "Full name cannot exceed 100 characters.";
  // Letters, spaces, apostrophes, periods and hyphens are accepted.
  if (!/^[A-Za-zÀ-ÖØ-öø-ÿ' .-]+$/.test(normalized)) {
    return "Enter a valid full name.";
  }
  return "";
};
const getEmailError = (value: string) => {
  const normalized = value.trim();
  if (!normalized) return "Email address is required.";
  if (normalized.length > 254) return "Email address is too long.";
  const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
  if (!emailPattern.test(normalized)) {
    return "Enter a valid email address.";
  }
  return "";
};
// Keep this validation local as well as in AppPhoneInput so this modal uses
// the exact same rules as Login even if an older shared PhoneInput is still
// present in the running application.
const getPhoneError = (
  countryCode: AppPhoneCountryCode,
  localNumber: string,
) => {
  const digits = String(localNumber || "").replace(/\D/g, "");
  const localDigits = digits.replace(/^0+/, "");
  if (!digits) {
    return "Phone number is required.";
  }
  if (countryCode === "ZA") {
    if (localDigits.length !== 9 || !/^[6-8]\d{8}$/.test(localDigits)) {
      return "Enter a valid South African phone number.";
    }
  }
  if (countryCode === "MW") {
    if (
      localDigits.length < 8 ||
      localDigits.length > 9 ||
      !/^[189]\d{7,8}$/.test(localDigits)
    ) {
      return "Enter a valid Malawi phone number.";
    }
  }
  return "";
};
const limitPhoneNumberLikeLogin = (value: string) => {
  const digits = String(value || "").replace(/\D/g, "");
  const maxLength = digits.startsWith("0") ? 10 : 9;
  return digits.slice(0, maxLength);
};
const getMessageError = (value: string) => {
  const normalized = value.trim();
  if (!normalized) return "Message cannot be empty.";
  if (normalized.length < 10) return "Message must be at least 10 characters.";
  if (normalized.length > 2000) return "Message cannot exceed 2000 characters.";
  return "";
};
const NotifyModal: React.FC<Props> = ({
  isOpen,
  onClose,
  subject = "Ask Us Anything",
}) => {
  const toast = useToast();
  const { theme } = useCustomTheme();
  const [form, setForm] = useState<NotifyForm>(EMPTY_FORM);
  const [phoneCountryCode, setPhoneCountryCode] =
    useState<AppPhoneCountryCode>("MW");
  const [touched, setTouched] = useState<TouchedState>(EMPTY_TOUCHED);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const validationErrors: ValidationErrors = {
    fullName: getFullNameError(form.fullName),
    email: getEmailError(form.email),
    phone: getPhoneError(phoneCountryCode, form.phone),
    message: getMessageError(form.message),
  };
  const isInvalid = {
    fullName: touched.fullName && Boolean(validationErrors.fullName),
    email: touched.email && Boolean(validationErrors.email),
    phone: touched.phone && Boolean(validationErrors.phone),
    message: touched.message && Boolean(validationErrors.message),
  };
  const handleChange = (field: keyof NotifyForm, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };
  const handleBlur = (field: keyof NotifyForm) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
  };
  const handlePhoneChange = (value: string) => {
    handleChange("phone", limitPhoneNumberLikeLogin(value));
    setTouched((prev) => ({ ...prev, phone: true }));
  };
  const resetForm = () => {
    setForm(EMPTY_FORM);
    setPhoneCountryCode("MW");
    setTouched(EMPTY_TOUCHED);
  };
  const handleClose = () => {
    if (isSubmitting) return;
    setTouched(EMPTY_TOUCHED);
    onClose();
  };
  const handleSubmit = async (event?: FormEvent<HTMLFormElement>) => {
    event?.preventDefault();
    if (isSubmitting) return;
    setTouched({
      fullName: true,
      email: true,
      phone: true,
      message: true,
    });
    // IMPORTANT: validate the values directly instead of relying on touched.
    // setTouched is asynchronous, so using touched here can allow invalid data
    // through on the first submit attempt.
    const hasValidationError = Boolean(
      validationErrors.fullName ||
      validationErrors.email ||
      validationErrors.phone ||
      validationErrors.message,
    );
    if (hasValidationError) {
      toast({
        title: "Please check the form",
        description:
          "Correct the highlighted fields before sending your message.",
        status: "warning",
        duration: 3500,
        isClosable: true,
      });
      return;
    }
    try {
      setIsSubmitting(true);
      const apiBase = String(process.env.REACT_APP_BACKEND_URL || "").replace(
        /\/+$/,
        "",
      );
      const baseUrl = `${apiBase}/api/v1/send-email`;
      const normalizedFullName = normalizeSpaces(form.fullName);
      const nameParts = normalizedFullName.split(" ");
      const firstName = nameParts.shift() || "";
      const lastName = nameParts.join(" ");
      const fullPhoneNumber = buildInternationalNumber(
        phoneCountryCode,
        form.phone,
      );
      await axios.post(baseUrl, {
        name: firstName,
        lastname: lastName,
        email: form.email.trim().toLowerCase(),
        phone: fullPhoneNumber,
        message:
          subject === "Ask Us Anything"
            ? form.message.trim()
            : `Subject: ${subject}\n\n${form.message.trim()}`,
      });
      toast({
        title: "Message Sent!",
        description: "Thank you for reaching out. We'll respond soon.",
        status: "success",
        duration: 5000,
        isClosable: true,
      });
      resetForm();
      onClose();
    } catch (error: any) {
      console.error("Failed to send:", error);
      const backendMessage =
        error?.response?.data?.message || error?.response?.data?.error || "";
      toast({
        title: "Failed to send message",
        description:
          typeof backendMessage === "string" && backendMessage.trim()
            ? backendMessage
            : "Please try again later.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };
  return (
    <Modal isOpen={isOpen} onClose={handleClose}>
      <ModalOverlay
        bg={theme.name === "dark" ? "blackAlpha.700" : "blackAlpha.500"}
      />
      <ModalContent
        p={4}
        bg={theme.surface}
        color={theme.text}
        border="1px solid"
        borderColor={theme.border}
        boxShadow={theme.shadowMd || "xl"}
      >
        <ModalHeader color={theme.text}>{subject}</ModalHeader>
        <ModalCloseButton color={theme.textMuted} isDisabled={isSubmitting} />
        <ModalBody>
          <form onSubmit={handleSubmit} noValidate>
            <FormControl isInvalid={isInvalid.fullName} isRequired mb={4}>
              <FormLabel color={theme.textMuted}>Full Name</FormLabel>
              <Input
                placeholder="Your full name"
                value={form.fullName}
                onChange={(e) => handleChange("fullName", e.target.value)}
                onBlur={() => handleBlur("fullName")}
                maxLength={100}
                autoComplete="name"
                bg={theme.inputBg}
                color={theme.text}
                borderColor={theme.inputBorder}
                _placeholder={{ color: theme.inputPlaceholder }}
                _hover={{ borderColor: theme.borderStrong }}
                _focus={{
                  borderColor: theme.inputBorderFocus,
                  boxShadow: `0 0 0 1px ${theme.inputBorderFocus}`,
                }}
              />
              <FormErrorMessage>{validationErrors.fullName}</FormErrorMessage>
            </FormControl>
            <FormControl isInvalid={isInvalid.email} isRequired mb={4}>
              <FormLabel color={theme.textMuted}>Email</FormLabel>
              <Input
                type="email"
                placeholder="you@example.com"
                value={form.email}
                onChange={(e) => handleChange("email", e.target.value)}
                onBlur={() => handleBlur("email")}
                maxLength={254}
                autoComplete="email"
                bg={theme.inputBg}
                color={theme.text}
                borderColor={theme.inputBorder}
                _placeholder={{ color: theme.inputPlaceholder }}
                _hover={{ borderColor: theme.borderStrong }}
                _focus={{
                  borderColor: theme.inputBorderFocus,
                  boxShadow: `0 0 0 1px ${theme.inputBorderFocus}`,
                }}
              />
              <FormErrorMessage>{validationErrors.email}</FormErrorMessage>
            </FormControl>
            <FormControl isInvalid={isInvalid.phone} isRequired mb={4}>
              <AppPhoneInput
                label="Phone Number"
                value={form.phone}
                countryCode={phoneCountryCode}
                placeholder="Phone number"
                onCountryChange={(value) => {
                  setPhoneCountryCode(value);
                  handleChange("phone", "");
                  setTouched((prev) => ({ ...prev, phone: true }));
                }}
                onNumberChange={handlePhoneChange}
                onBlur={() => handleBlur("phone")}
                theme={theme}
                labelColor={theme.textMuted}
                isRequired
                isInvalid={isInvalid.phone}
              />
              <FormErrorMessage>{validationErrors.phone}</FormErrorMessage>
            </FormControl>
            <FormControl isInvalid={isInvalid.message} isRequired mb={4}>
              <FormLabel color={theme.textMuted}>Message</FormLabel>
              <Textarea
                placeholder="Write your message here..."
                value={form.message}
                onChange={(e) => handleChange("message", e.target.value)}
                onBlur={() => handleBlur("message")}
                minH="110px"
                maxLength={2000}
                bg={theme.inputBg}
                color={theme.text}
                borderColor={theme.inputBorder}
                _placeholder={{ color: theme.inputPlaceholder }}
                _hover={{ borderColor: theme.borderStrong }}
                _focus={{
                  borderColor: theme.inputBorderFocus,
                  boxShadow: `0 0 0 1px ${theme.inputBorderFocus}`,
                }}
              />
              <FormErrorMessage>{validationErrors.message}</FormErrorMessage>
            </FormControl>
            <Button
              type="submit"
              width="100%"
              mt={4}
              isLoading={isSubmitting}
              isDisabled={isSubmitting}
              loadingText="Sending"
              bg={theme.primary}
              color={theme.textOnPrimary}
              _hover={{ bg: theme.primaryHover }}
            >
              Send Message
            </Button>
          </form>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
export default NotifyModal;
